/**
 * Extracts a user-friendly error message from an API error response.
 *
 * Handles multiple error formats:
 * - String detail: "Invalid credentials"
 * - Array detail: [{ msg: "Field required" }, { msg: "Invalid format" }]
 * - Object detail: { field: "error message" }
 * - Generic errors: { message: "Network error" }
 *
 * @param error - The error object from API call
 * @param fallbackMessage - Default message if no detail found
 * @returns User-friendly error message string
 *
 * @example
 * try {
 *   await api.call();
 * } catch (error) {
 *   const message = getErrorMessage(error, 'Operation failed');
 *   toast.error(message);
 * }
 */
export function getErrorMessage(error: unknown, fallbackMessage = 'An error occurred'): string {
  const apiError = error as { response?: { data?: { detail?: unknown } }; message?: string };
  // Check for API error detail
  if (apiError.response?.data?.detail) {
    const detail = apiError.response.data.detail;

    // String detail - most common case
    if (typeof detail === 'string') {
      return detail;
    }

    // Array detail - validation errors
    if (Array.isArray(detail)) {
      return detail.map(e => e.msg || e).join(', ');
    }

    // Object detail - try to stringify
    if (typeof detail === 'object') {
      return JSON.stringify(detail);
    }
  }

  // Check for generic error message
  if (apiError.message) {
    return apiError.message;
  }

  // Fallback
  return fallbackMessage;
}
